<template>
    <div class="assistent-goals">
        <div class="assistent-goals__header">
            <text-decorator :size="FontSize.md" weight="medium">
                Leaddoelen
            </text-decorator>

            <button-collapse
                v-if="unselectedGoals.length"
                :color="'primary'"
                :options="unselectedGoals"
                :variant="'text'"
                vertical-position="top-out"
                @click="addGoal"
            >
                <svg-icon
                    :icon="AddIcon"
                />
                Toevoegen
            </button-collapse>
        </div>

        <div class="assistent-goals__entries">
            <div
                v-for="(goal, index) in selectedGoals"
                :key="index"
                class="assistent-goals__entry"
            >
                <span>
                    {{ goal.label }}
                </span>
                <span
                    class="assistent-goals__entry-remove"
                    @click="removeGoal(goal.value)"
                    @keydown.enter="removeGoal(goal.value)"
                >
                    <svg-icon
                        :color="'gray-300'"
                        :icon="CloseIcon"
                        :size="IconSize.sm"
                    />
                </span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {
        ButtonCollapse,
        FontSize,
        IconSize,
        SvgIcon,
        TextDecorator,
    } from '@keyvue/one-ui';

    import { computed, type Ref, ref, watch } from 'vue';
    import { type Assistent, Goal } from '@keyvue/one-api-client';
    import CloseIcon from '@keyvue/one-material-icons/icons/close.svg?component';
    import AddIcon from '@keyvue/one-material-icons/icons/add.svg?component';

    const props = defineProps<{ modelValue: Assistent }>();
    const emit = defineEmits<{
        (e: 'update:modelValue', value: Assistent): void;
    }>();

    const assistent = ref(props.modelValue) as Ref<Assistent>;

    watch(props.modelValue, (value) => {
        assistent.value = value;
    });

    watch(assistent, (value: Assistent) => {
        emit('update:modelValue', value);
    }, { deep: true });

    const selectedGoalValues = computed(() => {
        return assistent.value.behavior.goals.map((goal) => goal.value);
    });

    const selectedGoals = computed(() => {
        return Goal.values.filter((goal) => selectedGoalValues.value.includes(goal.value));
    });

    const unselectedGoals = computed(() => {
        return Goal.values.filter((goal) => !selectedGoalValues.value.includes(goal.value));
    });

    function addGoal(value: string) {
        assistent.value.behavior.goals.push(new Goal(value));
    }

    function removeGoal(value: string) {
        assistent.value.behavior.goals = assistent.value.behavior.goals.filter((goal) => goal.value !== value);
    }
</script>

<style scoped lang="scss">
.assistent-goals {
    background-color: color(gray-100);
    padding: size(sm);
    display: flex;
    flex-direction: column;
    gap: size(xs);

    &__header {
        grid-column: span 2;
        display: grid;
        grid-template-columns: 1fr 1fr;
        align-items: center;
        min-height: 2rem;
    }

    &__entries {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: size(sm);
    }

    &__entry {
        font-size: 80%;
        width: min-content;
        display: inline-flex;
        background-color: white;
        border-radius: size(md);
        padding: size(xs) size(sm);
        gap: size(xs);
        align-items: center;

        &:nth-child(even) {
            justify-self: end;
        }
    }

    &__entry-remove {
        cursor: pointer;
    }
}
</style>
